import { Note, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { cn } from '@/lib/utils'
import { getInterviewerIntegrity } from './api'
import { count } from './vocab'

/**
 * One interviewer's record, as five counts and a window.
 *
 * ## Why there is no trust score
 *
 * The obvious thing to build here is a single 0–100 number, and it is the wrong
 * thing. Rolling five outcomes into one composite requires inventing weights
 * nobody agreed to — is one FABRICATED worth ten UNREACHABLE, or a hundred? —
 * and the answer then travels as if it were measured. Worse, the composite is
 * unarguable in the way that matters: an interviewer told "your score is 62"
 * cannot contest it, while one told "one of your four back-checks came back
 * fabricated" can.
 *
 * PollSphere's `readinessScore` is exactly that composite: a weighted blend of
 * things with no shared unit, presented as a fact and reproducible by nobody.
 * This refuses to be it. The counts are shown raw, and the reader does the
 * weighing — which is the part of the job that is theirs.
 *
 * ## A flag is a question; an outcome is an answer
 *
 * `concernsLast90Days` counts open WARN/HIGH flags — detections nobody has ruled
 * on. The outcome counts are findings a person made after re-contacting the
 * respondent. They are kept apart because a supervisor deciding whether to keep
 * somebody in the field needs to know which of the two they are looking at, and
 * a total that mixed them would flatter the interviewer with many verified
 * back-checks and punish the one nobody has checked at all.
 */
export function InterviewerPanel({ mobilizerId, fallbackName, dense }: {
  mobilizerId: string
  /** From the queue row. The integrity endpoint may not know the name; the queue does. */
  fallbackName?: string | null
  dense?: boolean
}) {
  const record = useLoad(() => getInterviewerIntegrity(mobilizerId), [mobilizerId])
  const r = record.data

  return (
    <div className={cn('erp-card', dense ? 'p-3' : 'p-4', 'space-y-2 min-w-0')}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-erp-sm font-medium truncate">
          {r?.mobilizerName ?? fallbackName ?? 'Interviewer'}
        </div>
        <div className="text-erp-xs text-muted-foreground tabular-nums shrink-0">
          {r ? `${count(r.backChecks)} back-check${r.backChecks === 1 ? '' : 's'}` : ''}
        </div>
      </div>

      <ErrorStrip message={record.error} />

      {record.loading && !r ? <LoadingBlock label="Loading the record…" /> : r && (
        <>
          {/*
            Fabricated first and in colour, alone. One accent per screen, and on a
            quality queue this is what it is spent on: a fabricated back-check is
            the only finding here that says the work did not happen.
          */}
          {r.fabricated > 0 && (
            <Note tone="bad">
              {count(r.fabricated)} back-check{r.fabricated === 1 ? '' : 's'} on this
              interviewer came back <strong className="font-semibold">FABRICATED</strong> — the
              respondent said the interview never happened. Every other interview they
              submitted on that poll was flagged at the same time and is out of the estimates
              until somebody decides each one.
            </Note>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Count label="Verified" value={r.verified} />
            <Count label="Partial mismatch" value={r.partialMismatch} />
            <Count label="Fabricated" value={r.fabricated} bad={r.fabricated > 0} />
            <Count label="Unreachable" value={r.unreachable} />
          </dl>

          <div className="pt-1 border-t border-border/60 flex items-baseline justify-between gap-3">
            <span className="text-erp-xs text-muted-foreground">
              Open concerns, last 90 days
            </span>
            <span className="text-erp-sm tabular-nums">{count(r.concernsLast90Days)}</span>
          </div>

          <p className="text-erp-xs text-muted-foreground">
            {r.backChecks === 0
              ? 'Nobody has re-contacted a respondent of theirs yet, so the outcome counts are '
                + 'empty for want of checking rather than because the work is clean. The '
                + 'concerns figure is detections nobody has ruled on.'
              : 'Five counts and a date window, deliberately not one score: a composite would '
                + 'need weights nobody agreed to, and this is a record somebody is entitled to '
                + 'argue with.'}
          </p>
        </>
      )}
    </div>
  )
}

function Count({ label, value, bad }: { label: string; value: number; bad?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 min-w-0">
      <dt className="text-erp-xs text-muted-foreground truncate">{label}</dt>
      <dd className={cn('text-erp-sm tabular-nums shrink-0', bad && 'text-rose-600 font-semibold')}>
        {count(value)}
      </dd>
    </div>
  )
}
